
import { Component, Vue } from 'vue-property-decorator';
import Malakai, { MalakaiConfig, MalakaiEvent } from './lib/Malakai';
import { CosCurve } from './lib/curves';
import SongChart from './components/SongChart.vue';
import { SongChartEventPayload } from './lib/SongChart';
import { BaseGenerator, MusicRNNGenerator, MusicVAEGenerator } from './lib/generators';
import { GenerationMode, BaseGeneratorOptions } from './lib/generators/BaseGenerator';
import leads from './lib/catalog/leads';
import * as Configs from './lib/configs';

const CHECKPOINTS_URL = 'https://raw.githubusercontent.com/tensorflow/magenta-js/master/music/checkpoints/checkpoints.json';

const configMap = Object.values(Configs)
  .reduce((prev, i) => prev.set(i.name, i), new Map<string, MalakaiConfig>());

enum GeneratorType {
  None = 'None',
  MusicRNN = 'MusicRNN',
  MusicVAE = 'MusicVAE',
}

type Checkpoint = {
  id: string;
  model: string;
  description: string;
  url: string;
  size_mb: string;
}

@Component({
  components: {
    SongChart,
  },
})
export default class App extends Vue {
  curve = new CosCurve([
    [0, 0.5],
    [0.33, 0.9],
    [0.66, 0.1],
    [1, 0.5],
  ]);

  malakai: Malakai|null = null;

  buffer = 0;

  progress = 0;

  isLoading = true;

  isMalakaiLoading = false;

  tickInterval = -1;

  config: MalakaiConfig = Configs.bansDimmer;

  configs: string[] = Object.values(Configs).map(({ name }) => name);

  leads: { name: string; value: string }[] = Object.entries(leads)
    .map(([name, value]) => ({ name, value }));

  generatorModels: { name: string; value: GeneratorType }[] = [
    { name: 'MusicRNN', value: GeneratorType.MusicRNN },
    { name: 'MusicVAE', value: GeneratorType.MusicVAE },
  ];

  modelCheckpoints: { [model: string]: Checkpoint[] } = {};

  generatorModes: { name: string; value: GenerationMode }[] = [
    { name: 'Rolling', value: GenerationMode.Rolling },
    { name: 'Looping', value: GenerationMode.Looping },
  ];

  async created() {
    await this.fetchCheckpoints();
    this.malakai = await this.initMalakai();
    this.isLoading = false;
  }

  async initMalakai() {
    const malakai = new Malakai(this.curve, this.config);
    await malakai.initialize();
    return malakai;
  }

  async play() {
    if (!this.malakai) return;
    if (this.malakai.state === 'stopped') {
      clearInterval(this.tickInterval);
      this.isMalakaiLoading = true;
      this.malakai = await this.initMalakai();
      this.isMalakaiLoading = false;
      this.tickInterval = setInterval(this.onTick, 1000 / 30);
      this.malakai.on(MalakaiEvent.BufferUpdate, (event) => {
        if (event) {
          this.buffer = event.progress;
        }
      });
      this.malakai.on(MalakaiEvent.Stop, () => {
        clearInterval(this.tickInterval);
      });
      this.malakai.start();
    } else {
      this.malakai.resume();
    }
  }

  pause() {
    if (this.malakai) {
      this.malakai.pause();
    }
  }

  stop() {
    if (this.malakai) {
      this.malakai.stop();
      this.progress = 0;
      this.buffer = 0;
    }
  }

  goToStart() {
    if (this.malakai) {
      this.malakai.setPosition(0);
    }
  }

  addControl() {
    let biggestGap = 0;
    let loneliestIndex = 0;
    let loneliestPoint: number[] = this.curve.controls[0];
    let previousPoint: number[] = loneliestPoint;
    this.curve.controls.forEach((p, i) => {
      const gap = p[0] - previousPoint[0];
      if (p[0] - previousPoint[0] > biggestGap) {
        biggestGap = gap;
        loneliestPoint = p;
        loneliestIndex = i;
      }
      previousPoint = p;
    });
    const nearestPoint = this.curve.controls[loneliestIndex - 1];
    const newPt = [
      (nearestPoint[0] + loneliestPoint[0]) / 2,
      (nearestPoint[1] + loneliestPoint[1]) / 2,
    ];
    this.curve.controls.splice(loneliestIndex, 0, newPt);
  }

  removeControl() {
    if (this.curve.controls.length < 3) return;
    let smallestGap = Infinity;
    let leastLonelyIndex = 0;
    let leastLonelyPoint: number[] = this.curve.controls[0];
    let previousPoint: number[] = leastLonelyPoint;
    this.curve.controls.forEach((p, i) => {
      const gap = p[0] - previousPoint[0];
      if (i > 0 && i < this.curve.controls.length - 1 && gap < smallestGap) {
        smallestGap = gap;
        leastLonelyPoint = p;
        leastLonelyIndex = i;
      }
      previousPoint = p;
    });
    this.curve.controls.splice(leastLonelyIndex, 1);
  }

  setSliderValue(key: string, value: number) {
    if (this.malakai) {
      this.malakai.setSliderValue(key, value);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getGeneratorType(generator: Generator): GeneratorType {
    if (generator instanceof MusicRNNGenerator) {
      return GeneratorType.MusicRNN;
    }
    if (generator instanceof MusicVAEGenerator) {
      return GeneratorType.MusicVAE;
    }
    return GeneratorType.None;
  }

  getGeneratorCheckpoint(generator: BaseGenerator): Checkpoint|null {
    if (generator instanceof MusicRNNGenerator && this.modelCheckpoints.MusicRNN) {
      return this.modelCheckpoints.MusicRNN.find(({ url }) => url === generator.checkpoint) || null;
    }
    if (generator instanceof MusicVAEGenerator && this.modelCheckpoints.MusicVAE) {
      return this.modelCheckpoints.MusicVAE.find(({ url }) => url === generator.checkpoint) || null;
    }
    return null;
  }

  async fetchCheckpoints() {
    const checkpoints = await (await fetch(CHECKPOINTS_URL)).json() as Checkpoint[];
    checkpoints.forEach((checkpoint) => {
      if (this.modelCheckpoints[checkpoint.model]) {
        this.modelCheckpoints[checkpoint.model].push(checkpoint);
      } else {
        this.modelCheckpoints[checkpoint.model] = [checkpoint];
      }
    });
  }

  onTick() {
    if (this.malakai) {
      this.progress = this.malakai.progress;
    }
  }

  onCurveUpdate({ curve }: SongChartEventPayload) {
    if (this.malakai) {
      this.curve = curve;
      this.malakai.setCurve(this.curve);
    }
  }

  onGeneratorModelSelect(generatorName: string, type: GeneratorType) {
    if (!this.malakai || !this.config.generators) return;
    const generatorConfig = this.config.generators.find(({ name }) => name === generatorName);
    if (generatorConfig) {
      const currentGenerator = generatorConfig.generator;
      const generatorOptions: BaseGeneratorOptions = {
        mode: currentGenerator.mode,
        tempo: currentGenerator.tempo,
        sequenceDuration: currentGenerator.sequenceDuration,
        lookbackDuration: currentGenerator.lookbackDuration,
      };
      let newGenerator: BaseGenerator|null = null;
      switch (type) {
        case GeneratorType.MusicRNN:
          newGenerator = new MusicRNNGenerator({
            ...generatorOptions,
            checkpoint: this.modelCheckpoints.MusicRNN[0].url,
          });
          break;
        case GeneratorType.MusicVAE:
          newGenerator = new MusicVAEGenerator({
            ...generatorOptions,
            checkpoint: this.modelCheckpoints.MusicVAE[0].url,
          });
          break;
        default: break;
      }
      if (newGenerator) {
        generatorConfig.generator = newGenerator;
      }
    }
  }

  onConfigSelect(configName: string) {
    const config = configMap.get(configName);
    if (config) {
      this.config = config;
      this.initMalakai();
    } else {
      throw new Error(`Config ${configName} not found.`);
    }
  }
}
